/**
 * Shared IANA-timezone option builder.
 *
 * Consumed by:
 *  - `components/user-profile/user-profile-page.tsx` — user timezone picker.
 *  - `components/organization/...` — organization timezone picker.
 *
 * Each option carries the IANA value, a display label, a short
 * abbreviation (e.g. CET), and the current UTC offset — pre-computed
 * once per render so the consumer's `useMemo` over the full list is cheap.
 *
 * The runtime gate around `Intl.supportedValuesOf` keeps us safe on older
 * environments — `UTC` is the fallback so the picker always renders at
 * least one valid option.
 */

const SUPPORTED_TIMEZONES =
  typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function"
    ? [...Intl.supportedValuesOf("timeZone")].sort((a, b) => a.localeCompare(b))
    : ["UTC"]

const getOffsetMinutes = (date: Date, timeZone: string): number => {
  try {
    const localeString = date.toLocaleString("en-US", { timeZone, hour12: false })
    const localTime = new Date(localeString)
    const utcString = date.toLocaleString("en-US", { timeZone: "UTC", hour12: false })
    const utcTime = new Date(utcString)
    const diffMinutes = Math.round((localTime.getTime() - utcTime.getTime()) / 60000)
    // Some IANA zones round-trip through `toLocaleString` to a string that
    // `new Date(...)` can't reparse, yielding NaN getTime() — fall back to 0
    // so the picker still renders the option (sorted at the UTC boundary).
    return Number.isFinite(diffMinutes) ? diffMinutes : 0
  } catch {
    return 0
  }
}

const formatOffset = (minutes: number): string => {
  const sign = minutes >= 0 ? "+" : "-"
  const absMinutes = Math.abs(minutes)
  const hours = Math.floor(absMinutes / 60)
  const mins = absMinutes % 60
  return `${sign}${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`
}

export interface TimezoneOption {
  value: string
  label: string
  abbreviation: string
  offset: string
  offsetMinutes: number
  searchText: string
}

export const sortTimezoneOptions = (options: TimezoneOption[]): TimezoneOption[] =>
  [...options].sort((a, b) => {
    if (a.offsetMinutes === b.offsetMinutes) {
      return a.value.localeCompare(b.value)
    }
    return a.offsetMinutes - b.offsetMinutes
  })

export const createTimezoneOption = (
  timeZone: string,
  referenceDate: Date,
): TimezoneOption | null => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
    const parts = formatter.formatToParts(referenceDate)
    const tzName = parts.find((part) => part.type === "timeZoneName")?.value
    const abbreviation = tzName && tzName !== timeZone ? tzName : timeZone
    const offsetMinutes = getOffsetMinutes(referenceDate, timeZone)
    const offset = formatOffset(offsetMinutes)

    return {
      value: timeZone,
      label: `${timeZone} (${abbreviation})`,
      abbreviation,
      offset,
      offsetMinutes,
      searchText: `${timeZone.toLowerCase()} ${abbreviation.toLowerCase()} ${offset.toLowerCase()}`,
    }
  } catch {
    return null
  }
}

export const buildTimezoneOptions = (): TimezoneOption[] => {
  const referenceDate = new Date()
  const candidates = SUPPORTED_TIMEZONES.length > 0 ? SUPPORTED_TIMEZONES : ["UTC"]

  const options = candidates
    .map((timeZone) => createTimezoneOption(timeZone, referenceDate))
    .filter((option): option is TimezoneOption => option !== null)

  if (options.length === 0) {
    const fallback = createTimezoneOption("UTC", referenceDate)
    return fallback ? [fallback] : []
  }

  return sortTimezoneOptions(options)
}

/**
 * Resolve a custom (not-in-the-base-list) timezone string and merge it
 * into the option list. Used by pickers that may render a value that
 * `Intl.supportedValuesOf` doesn't return on the current runtime.
 */
export const withCustomTimezone = (
  baseOptions: TimezoneOption[],
  value: string | null | undefined,
): TimezoneOption[] => {
  if (!value) return baseOptions
  if (baseOptions.some((option) => option.value === value)) return baseOptions
  const customOption = createTimezoneOption(value, new Date())
  if (!customOption) return baseOptions
  return sortTimezoneOptions([...baseOptions, customOption])
}
